import { useState, type PropsWithChildren, type FormEvent } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import type { Permission, Rule } from "@/types";
import { useAddPermissionMutation } from "@/hooks/rules/use-add-permission-mutation";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { addBasicGrants, isTenantGrant, moduleLabel, permissionGroups, permissionLabel, roleLabel } from "@/hooks/rules/permission-ux";

type Props = PropsWithChildren & {
  rule: Rule;
  permissions: Permission[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

export function FormAddPermissionRule({ rule, permissions, children, isLoading: catalogLoading, isError, onRetry }: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [saveError, setSaveError] = useState(false);
  const { toast } = useToast();
  const { mutateAsync, isLoading: saving } = useAddPermissionMutation();
  const blocked = saving || catalogLoading || isError;
  const groups = permissionGroups(permissions, search);

  function changeOpen(next: boolean) {
    if (saving) return;
    if (next) {
      setSelected(rule.permissions.filter(isTenantGrant).map(permission => permission.id));
      setSearch("");
      setSaveError(false);
    }
    setOpen(next);
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (blocked) return;
    setSaveError(false);
    try {
      await mutateAsync({ id: rule.id, permission: selected });
    } catch {
      setSaveError(true);
      toast({ title: "Não foi possível salvar", description: "Suas escolhas foram mantidas. Tente novamente.", variant: "destructive" });
      return;
    }
    toast({ title: "Permissões atualizadas" });
    setOpen(false);
  }

  return <Dialog open={open} onOpenChange={changeOpen}>
    <DialogTrigger asChild>{children}</DialogTrigger>
    <DialogContent className="flex max-h-[90dvh] max-w-3xl flex-col rounded-2xl bg-app-surface p-5 sm:p-6" showCloseButton={!saving}>
      <DialogHeader className="shrink-0">
        <DialogTitle>Permissões · {roleLabel(rule.name)}</DialogTitle>
        <DialogDescription>Escolha o acesso por módulo. Ajustes avançados permitem selecionar cada ação.</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="flex min-h-0 flex-col gap-4 overflow-hidden">
        <label className="shrink-0 space-y-2 text-sm text-app-fg">
          <span>Buscar módulo ou ação</span>
          <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Ex.: odontologia, ortodontia, agenda" />
        </label>
        <p className="shrink-0 text-sm text-app-fg" aria-live="polite">{selected.length} permissões selecionadas. Finalizar, exportar e excluir exigem seleção individual.</p>
        {isError ? <div role="alert" className="space-y-2 text-sm text-app-fg">
          <p>Não foi possível carregar as permissões. Suas escolhas foram mantidas.</p>
          <Button type="button" variant="outline" onClick={onRetry}>Tentar novamente</Button>
        </div> : catalogLoading ? <p role="status">Carregando permissões…</p> : null}
        <fieldset disabled={blocked} className="max-h-[45dvh] min-h-0 overflow-y-auto disabled:opacity-60">
          <legend className="sr-only">Acesso por módulo</legend>
          {!isError && !catalogLoading && groups.length === 0 && <p className="py-6 text-sm text-app-fg">Nenhum módulo encontrado.</p>}
          {groups.map(([module, grants]) => {
            const count = grants.filter(grant => selected.includes(grant.id)).length;
            return <section key={module} aria-label={moduleLabel(module)} className="space-y-3 border-b border-app-border py-4 first:pt-0">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-medium text-app-fg-strong">{moduleLabel(module)}</h3>
                <span className="text-sm tabular-nums text-app-fg">{count} de {grants.length}</span>
              </div>
              {(module === "dentistry" || module === "orthodontics") && <p className="text-sm text-app-fg">As permissões definem o acesso da equipe. O módulo precisa estar habilitado para a clínica.</p>}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => setSelected(current => current.filter(id => !grants.some(grant => grant.id === id)))}>Sem acesso</Button>
                <Button type="button" variant="outline" onClick={() => setSelected(current => addBasicGrants(current, grants, "read"))}>Adicionar consulta</Button>
                <Button type="button" variant="outline" onClick={() => setSelected(current => addBasicGrants(current, grants, "edit"))}>Adicionar edição</Button>
              </div>
              <details>
                <summary className="flex min-h-10 cursor-pointer items-center text-sm text-brand focus-visible:outline focus-visible:outline-brand">Ajustes avançados · {moduleLabel(module)}</summary>
                <div className="grid gap-x-4 sm:grid-cols-2">
                  {grants.map(grant => <label key={grant.id} className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-app-fg">
                    <Checkbox checked={selected.includes(grant.id)} onCheckedChange={checked => setSelected(current => checked === true ? [...new Set([...current, grant.id])] : current.filter(id => id !== grant.id))} />
                    {permissionLabel(grant)}
                  </label>)}
                </div>
              </details>
            </section>;
          })}
        </fieldset>
        {selected.length === 0 && <p className="text-sm text-app-fg">Este perfil ficará sem permissões.</p>}
        {saveError && <p role="alert" className="text-sm text-app-danger">Não foi possível salvar. Revise as escolhas ou tente novamente.</p>}
        <div className="flex shrink-0 justify-end gap-3 border-t border-app-border pt-4">
          <Button type="button" variant="outline" disabled={saving} onClick={() => changeOpen(false)}>Cancelar</Button>
          <Button type="submit" variant="petrol" disabled={blocked} isLoading={saving}>Salvar permissões</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
}
